import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  id: string;
  name: string;
  set: { name: string; slug?: string };
  rarity?: string;
  images?: { small?: string };
}

interface CardSearchProps {
  onClose?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export default function CardSearch({ onClose, placeholder = "Search cards by name or ID...", autoFocus = true }: CardSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [setsIndex, setSetsIndex] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Load sets index on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/cards/index.json');
        const idx = await res.json();
        setSetsIndex(idx);
      } catch (err) {
        console.error('Failed to load sets index:', err);
      }
    })();
  }, []);

  // Auto-focus input
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const searchCards = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || setsIndex.length === 0) {
      setResults([]);
      return;
    }

    setLoading(true);
    const query = searchQuery.toLowerCase().trim();
    const foundCards: SearchResult[] = [];

    try {
      // Search across all sets
      const searchPromises = setsIndex.map(async (set) => {
        try {
          const res = await fetch(`/cards/${set.slug}.json`);
          const cards = await res.json();
          
          return cards
            .filter((card: any) => {
              // Search by ID (exact match)
              if (card.id.toLowerCase() === query) {
                return true;
              }
              
              // Search by name (partial match)
              if (card.name.toLowerCase().includes(query)) {
                return true;
              }
              
              // Search by card number
              if (card.number && card.number.toString().toLowerCase().includes(query)) {
                return true;
              }
              
              return false;
            })
            .map((card: any) => ({
              id: card.id,
              name: card.name,
              set: { 
                name: card.set?.name || set.name,
                slug: set.slug 
              },
              rarity: card.rarity,
              images: card.images,
            }));
        } catch (err) {
          console.warn(`Failed to search in set ${set.slug}:`, err);
          return [];
        }
      });

      const results = await Promise.all(searchPromises);
      const allResults = results.flat();
      
      // Sort results: exact ID matches first, then by name
      allResults.sort((a, b) => {
        if (a.id.toLowerCase() === query && b.id.toLowerCase() !== query) return -1;
        if (b.id.toLowerCase() === query && a.id.toLowerCase() !== query) return 1;
        return a.name.localeCompare(b.name);
      });

      foundCards.push(...allResults.slice(0, 20)); // Limit to 20 results
    } catch (err) {
      console.error('Search failed:', err);
    }

    setResults(foundCards);
    setSelectedIndex(-1);
    setLoading(false);
  }, [setsIndex]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCards(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, searchCards]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleCardSelect(results[selectedIndex]);
      } else if (results.length > 0) {
        handleCardSelect(results[0]);
      }
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  const handleCardSelect = (card: SearchResult) => {
    navigate(`/card/${encodeURIComponent(card.id)}`);
    onClose?.();
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div className="absolute top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          {results.map((card, index) => (
            <div
              key={card.id}
              onClick={() => handleCardSelect(card)}
              className={`p-3 cursor-pointer border-b border-gray-700 last:border-b-0 hover:bg-gray-700 ${
                index === selectedIndex ? 'bg-gray-700' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                {card.images?.small && (
                  <img
                    src={card.images.small}
                    alt={card.name}
                    className="w-12 h-16 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{card.name}</div>
                  <div className="text-sm text-gray-400 truncate">
                    {card.set.name} • {card.id}
                  </div>
                  {card.rarity && (
                    <div className="text-xs text-gray-500">{card.rarity}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {query.trim() && !loading && results.length === 0 && (
        <div className="absolute top-full mt-1 w-full bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3 z-50">
          <div className="text-gray-400 text-sm">No cards found for "{query}"</div>
        </div>
      )}
    </div>
  );
}
